import { useMemo, useRef } from 'react';
import { useReactFlow, type Edge, type Node } from '@xyflow/react';
import { TipoAlvo, TipoNota, uuidDoModulo, type AlvoDto, type AtividadeDto, type ConectorDto, type ModuloLayoutDto, type ModuloLicitacao } from '@nexital/shared';
import { moduloDoAlvo } from '../../domain/licitacao';
import { posicaoSemTrocarAlvo } from '../../domain/notas';
import { useNotas } from '../notas/contexto-notas';
import { montarNosNotas, type DadosSeta, type NoNotaTipo } from '../notas/montar-nos-notas';
import type { Ferramenta } from '../quadro/BarraFerramentas';
import { pontoDoEvento } from '../quadro/use-soltura';
import { idPontaItem } from './PontaItem';

export type NoModulo = Node<{ modulo: ModuloLicitacao }, 'modulo'>;
export type NoLicitacao = NoModulo | NoNotaTipo;
type Ponta = { no: string; ponta?: string };

const uuidDoNo = (id: string) => id.slice(2);

/** Onde a seta encosta: na nota, no módulo inteiro ou na ponta do item dentro do módulo. */
function ponta(alvo: AlvoDto, atividades: AtividadeDto[]): Ponta | null {
  if (alvo.tipo === TipoAlvo.Nota) return { no: `n:${alvo.uuid}` };
  const modulo = moduloDoAlvo(alvo, atividades);
  if (modulo === null) return null;
  return alvo.tipo === TipoAlvo.Modulo ? { no: String(modulo) } : { no: String(modulo), ponta: idPontaItem(alvo.uuid) };
}

function aresta(seta: ConectorDto, atividades: AtividadeDto[]): Edge<DadosSeta, 'seta'> | null {
  const [origem, destino] = [ponta(seta.origem, atividades), ponta(seta.destino, atividades)];
  if (!origem || !destino) return null;
  return {
    id: `s:${seta.uuid}`,
    type: 'seta',
    source: origem.no,
    sourceHandle: origem.ponta,
    target: destino.no,
    targetHandle: destino.ponta,
    data: { rotulo: seta.rotulo, setaUuid: seta.uuid },
  };
}

/** Posição do módulo em que o alvo (módulo ou item) é desenhado; null se não está no layout. */
function posicaoNoLayout(layout: ModuloLayoutDto[], atividades: AtividadeDto[]) {
  const porModulo = new Map(layout.map((m) => [m.modulo, m]));
  return (alvo: AlvoDto) => {
    const modulo = moduloDoAlvo(alvo, atividades);
    const lugar = modulo === null ? undefined : porModulo.get(modulo);
    return lugar ? { x: lugar.x, y: lugar.y } : null;
  };
}

/** Post-its e setas da licitação (os de lista ficam no módulo Notas): presos a módulos e itens (posição relativa). */
export function useNotasLicitacao(layout: ModuloLayoutDto[], atividades: AtividadeDto[]) {
  const notas = useNotas((s) => s.notas);
  const setas = useNotas((s) => s.setas);
  return useMemo(() => {
    const postIts = notas.filter((n) => n.tipo !== TipoNota.DoProcesso);
    const { nos } = montarNosNotas(postIts, [], posicaoNoLayout(layout, atividades));
    const arestas = setas.map((s) => aresta(s, atividades)).filter((a): a is Edge<DadosSeta, 'seta'> => a !== null);
    return { nos, arestas };
  }, [notas, setas, layout, atividades]);
}

/** Alvo clicado: a nota, o item dentro do módulo (data-alvo-*) ou o módulo inteiro. */
function alvoDoClique(evento: React.MouseEvent, no: NoLicitacao): AlvoDto {
  if (no.type === 'nota') return { tipo: TipoAlvo.Nota, uuid: uuidDoNo(no.id) };
  const item = (evento.target as Element).closest<HTMLElement>('[data-alvo-uuid]');
  if (item) return { tipo: Number(item.dataset.alvoTipo) as TipoAlvo, uuid: item.dataset.alvoUuid! };
  return { tipo: TipoAlvo.Modulo, uuid: uuidDoModulo(no.data.modulo) };
}

/** O que está sob o ponteiro ao soltar um post-it (ignorando o próprio post-it): item, módulo ou nada. */
function alvoSobPonteiro(x: number, y: number): { alvo: AlvoDto; modulo: ModuloLicitacao } | null {
  const embaixo = document.elementsFromPoint(x, y).filter((e) => !e.closest('.react-flow__node-nota'));
  const noModulo = embaixo.map((e) => e.closest<HTMLElement>('.react-flow__node-modulo')).find(Boolean);
  if (!noModulo) return null;
  const modulo = Number(noModulo.dataset.id) as ModuloLicitacao;
  const item = embaixo.map((e) => e.closest<HTMLElement>('[data-alvo-uuid]')).find(Boolean);
  const alvo = item ? { tipo: Number(item.dataset.alvoTipo) as TipoAlvo, uuid: item.dataset.alvoUuid! } : { tipo: TipoAlvo.Modulo, uuid: uuidDoModulo(modulo) };
  return { alvo, modulo };
}

type Opcoes = { ferramenta: Ferramenta; definirFerramenta: (f: Ferramenta) => void; layout: ModuloLayoutDto[]; atividades: AtividadeDto[] };

/** Ferramentas no quadro da licitação: post-it onde se clica, seta com dois cliques e soltar prendendo. */
export function useInteracaoLicitacao({ ferramenta, definirFerramenta, layout, atividades }: Opcoes) {
  const { criarNota, alterarNota, criarSeta } = useNotas((s) => s);
  const { screenToFlowPosition } = useReactFlow();
  const origem = useRef<AlvoDto | null>(null);

  const aoClicarNoVazio = (evento: React.MouseEvent) => {
    if (ferramenta !== 'nota') return;
    const { x, y } = screenToFlowPosition({ x: evento.clientX, y: evento.clientY });
    void criarNota({ tipo: TipoNota.PostIt, texto: '', x, y });
    definirFerramenta('selecao');
  };

  const aoClicarNo = (evento: React.MouseEvent, no: NoLicitacao) => {
    if (ferramenta !== 'conector') return;
    const alvo = alvoDoClique(evento, no);
    if (!origem.current || origem.current.uuid === alvo.uuid) return void (origem.current = alvo);
    void criarSeta({ origem: origem.current, destino: alvo });
    origem.current = null;
    definirFerramenta('selecao');
  };

  /**
   * Solto sobre um módulo ou item: fica preso a ele, com a posição relativa ao módulo em que o alvo é desenhado (o
   * mesmo de useNotasLicitacao); item sem módulo conhecido prende ao módulo sob o ponteiro. Fora deles, solto.
   * Quem não é o dono só move, no mesmo alvo (RQ-NOT-010).
   */
  const soltarNota = (evento: MouseEvent | TouchEvent, nota: NoNotaTipo) => {
    const { podeEditar, presoA } = nota.data.nota;
    if (!podeEditar) return void alterarNota(uuidDoNo(nota.id), posicaoSemTrocarAlvo(nota.position, presoA, posicaoNoLayout(layout, atividades)));
    const ponto = pontoDoEvento(evento);
    const sob = ponto && alvoSobPonteiro(ponto.x, ponto.y);
    if (!sob) return void alterarNota(uuidDoNo(nota.id), { ...nota.position, presoA: null });
    const doAlvo = moduloDoAlvo(sob.alvo, atividades);
    const alvo = doAlvo === null ? { tipo: TipoAlvo.Modulo, uuid: uuidDoModulo(sob.modulo) } : sob.alvo;
    const lugar = layout.find((m) => m.modulo === (doAlvo ?? sob.modulo));
    if (!lugar) return void alterarNota(uuidDoNo(nota.id), { ...nota.position, presoA: null });
    void alterarNota(uuidDoNo(nota.id), { x: nota.position.x - lugar.x, y: nota.position.y - lugar.y, presoA: alvo });
  };

  return { aoClicarNoVazio, aoClicarNo, soltarNota };
}
